import { Badge } from '@/components/ui/badge';
import { stateLabel, type TicketBusinessState } from '@/lib/tickets/businessStateMachine';

interface Props {
  state?: TicketBusinessState | string | null;
  /** Identifies which status doctrine this badge represents (per Three-Status Clarity rule). */
  showDoctrineLabel?: boolean;
}

/**
 * Phase 16 — Renders the commercial workflow state of a ticket.
 *
 * Per the Three-Status Clarity doctrine, this badge ALWAYS represents
 * `ticket_business_state.current_state` (commercial workflow), distinct from
 * `tickets.status` (operational) and `customer_invoices.status` (financial document).
 */
export function TicketBusinessStateBadge({ state, showDoctrineLabel = false }: Props) {
  if (!state) return null;
  const variant = (() => {
    switch (state) {
      case 'paid':
      case 'closed':
        return 'default' as const;
      case 'invoiced':
      case 'ready_to_invoice':
        return 'secondary' as const;
      case 'work_complete':
      case 'in_progress':
      case 'approved':
        return 'outline' as const;
      case 'voided':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  })();

  return (
    <div className="inline-flex items-center gap-1.5">
      {showDoctrineLabel && (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Workflow
        </span>
      )}
      <Badge variant={variant}>{stateLabel(state as TicketBusinessState)}</Badge>
    </div>
  );
}
